import { useCallback, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import {
  IconPostPauseOutline,
  IconPostPlayOutline
} from 'assets/svg/sprite'
import { useGlobalVideo } from 'context/globalVideoContext'
import VideoSurface from 'context/VideoSurface'
import ProgressBar from 'features/VideoPlayer/ProgressBar/ProgressBar'
import { useVideoPlayedTracking } from 'helpers/hooks'
import { formatVideoTime } from 'lib/dayjs'
import { usePreviewStore } from 'store/previewStore'

import { type IProps } from './VideoFile.types'

export const VideoFile = ({
  file,
  postIndex,
  fileIndex,
  postData,
  dataQuery,
  previewData
}: IProps) => {
  const [videoPlaying, setVideoPlaying] = useState(false)
  const [playWhenAttached, setPlayWhenAttached] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)

  const { t } = useTranslation()
  const previewOpen = usePreviewStore(state => state.previewOpen)

  const {
    activeMediaId,
    setActiveMediaId,
    play,
    pause,
    seekTo,
    getVideoElement
  } = useGlobalVideo()

  const videoOwnerId = postData?.user_id ?? postData?.user?.id
  const orientation = file?.orientation
  const isPortrait = orientation === 'Portrait'
  const orientationClass =
    file?.orientation === 'Portrait'
      ? 'post__files__file--portrait'
      : file?.orientation === 'Landscape'
        ? 'post__files__file--landscape'
        : ''
  const playbackUrl = file.preview.src
  const mediaId = String(file.id ?? playbackUrl)

  /*
   * Feed owns the shared surface only while this media is selected
   * and the fullscreen preview is not currently open.
   */
  const ownsSurface = activeMediaId === mediaId && !previewOpen

  const getOwnedVideoElement = useCallback(
    () => (ownsSurface ? getVideoElement() : null),
    [ownsSurface, getVideoElement]
  )

  /*
   * Keep the full source duration as the displayed/tracking
   * denominator, matching the previous VideoFile behavior.
   */
  const videoDuration =
    typeof file?.duration === 'number' && file.duration > 0
      ? file.duration
      : undefined

  /*
   * This component is explicitly preview playback.
   */
  const { updateMaxPlayedS, sendPlayedOnEnded } = useVideoPlayedTracking(
    true,
    playbackUrl,
    file.id,
    videoOwnerId,
    videoDuration,
    true
  )

  /*
   * Sync React state from the actual shared <video>.
   */
  useEffect(() => {
    if (!ownsSurface) return

    const video = getVideoElement()
    if (!video) return

    const syncState = () => {
      const time = video.currentTime || 0
      const isPlaying = !video.paused && !video.ended

      setVideoPlaying(isPlaying)
      setCurrentTime(time)

      if (time > 0) {
        updateMaxPlayedS(time)
      }
    }

    const handleTimeUpdate = () => {
      const time = video.currentTime || 0

      setCurrentTime(time)

      if (time > 0) {
        updateMaxPlayedS(time)
      }
    }

    const handlePlay = () => {
      setVideoPlaying(true)
    }

    const handlePause = () => {
      setVideoPlaying(false)
    }

    const handleEnded = () => {
      setVideoPlaying(false)
      sendPlayedOnEnded()
    }

    syncState()

    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)
    video.addEventListener('ended', handleEnded)

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate)
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
      video.removeEventListener('ended', handleEnded)
    }
  }, [ownsSurface, getVideoElement, updateMaxPlayedS, sendPlayedOnEnded])

  /*
   * If another video becomes active, clear this component's
   * local playback state.
   */
  useEffect(() => {
    if (activeMediaId === mediaId) return

    setVideoPlaying(false)
    setPlayWhenAttached(false)
    setCurrentTime(0)
  }, [activeMediaId, mediaId])

  /*
   * If this surface has just been attached because the user
   * pressed play, start playback after attachment completes.
   */
  useEffect(() => {
    if (!ownsSurface || !playWhenAttached) return

    setPlayWhenAttached(false)

    const video = getVideoElement()

    if (
      video &&
      (
        video.ended ||
        (
          Number.isFinite(video.duration) &&
          video.duration > 0 &&
          video.currentTime >= video.duration - 0.1
        )
      )
    ) {
      seekTo(0, 'seconds')
    }

    void play()
  }, [ownsSurface, playWhenAttached, getVideoElement, seekTo, play])

  /*
   * Reset UI state when the preview source itself changes.
   */
  useEffect(() => {
    if (!ownsSurface) return

    setCurrentTime(0)
    setVideoPlaying(false)
  }, [playbackUrl, ownsSurface])

  const handleTogglePlay = (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    event.stopPropagation()

    /*
     * First click selects this media. VideoSurface will then
     * attach the shared <video>, and the effect above starts it.
     */
    if (activeMediaId !== mediaId) {
      setPlayWhenAttached(true)
      setActiveMediaId(mediaId)
      return
    }

    if (videoPlaying) {
      pause()
      return
    }

    const video = getVideoElement()

    if (
      video &&
      (
        video.ended ||
        (
          Number.isFinite(video.duration) &&
          video.duration > 0 &&
          video.currentTime >= video.duration - 0.1
        )
      )
    ) {
      seekTo(0, 'seconds')
    }

    void play()
  }

  const handleOnSeekChange = (fraction: number) => {
    if (!ownsSurface) return

    const seekFraction = fraction > 1 ? fraction / 100 : fraction

    seekTo(
      Math.max(0, Math.min(1, seekFraction)),
      'fraction'
    )
  }

  const handleClick = () => {
    const fileType = 'video'
    const {
      minimized,
      updateSelectedPost,
      handleMinimize,
      addModal
    } = previewData

    if (minimized) {
      updateSelectedPost(
        postIndex,
        fileIndex,
        fileType,
        postData,
        dataQuery
      )

      handleMinimize(false)
      return
    }

    addModal(
      postIndex,
      fileIndex,
      fileType,
      postData,
      dataQuery
    )
  }

  const poster = postData?.videos?.length
    ? file.active_thumb
    : (
        postData?.videos_preview?.find(
          (video: any) => video.id === file.id
        )?.active_thumb ?? file.active_thumb
      )

  return (
    <div
      className={`post__files__file post__files__file--img ${orientationClass}`}
      onClick={handleClick}
    >
      {/*
       * Keep showing the poster whenever this VideoFile does not
       * currently own the shared global video element.
       */}
      {!ownsSurface && poster && (
        <img
          src={poster}
          alt=''
          className='preview__file__video'
        />
      )}

      <VideoSurface
        ownerType='post'
        ownerId={mediaId}
        mediaId={mediaId}
        videoId={String(file.id)}
        videoUserId={
          videoOwnerId != null
            ? String(videoOwnerId)
            : undefined
        }
        url={playbackUrl}
        cloudfront={
          'cloudfront' in file.preview
            ? file.preview.cloudfront
            : null
        }
        poster={poster}
        active={ownsSurface}
        className='postVideo__videoSurface'
      />

      <div className='post__files__file__duration'>
        {currentTime > 0 ? (
          <>
            {formatVideoTime(Math.trunc(currentTime))}{' '}/
          </>
        ) : null}

        {formatVideoTime(videoDuration ?? 0)}
      </div>

      <ProgressBarWrapper
        onClick={event => {
          event.stopPropagation()
        }}
        onMouseDown={event => {
          event.stopPropagation()
        }}
        onTouchStart={event => {
          event.stopPropagation()
        }}
      >
        <Progress>
          <ProgressBar
            getVideoElement={getOwnedVideoElement}
            onSeek={handleOnSeekChange}
          />
        </Progress>
      </ProgressBarWrapper>

      {isPortrait && (
        <PortraitCtaCenter>
          <div
            className='post__cta__icon'
            onClick={handleTogglePlay}
          >
            {videoPlaying ? (
              <IconPostPauseOutline />
            ) : (
              <IconPostPlayOutline />
            )}
          </div>

          {!videoPlaying && (
            <p className='post__cta__text'>
              {t('premium')}
            </p>
          )}
        </PortraitCtaCenter>
      )}

      <div className='post__cta'>
        {!isPortrait && (
          <div
            className='post__cta__icon'
            onClick={handleTogglePlay}
          >
            {videoPlaying ? (
              <IconPostPauseOutline />
            ) : (
              <IconPostPlayOutline />
            )}
          </div>
        )}

        <div
          className={`post__cta__content ${
            videoPlaying ? 'post__cta__content--hidden' : ''
          }`}
        >
          {!isPortrait && (
            <p className='post__cta__text'>
              {t('premium')}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

const PortraitCtaCenter = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 6;

  display: flex;
  flex-direction: column;
  align-items: center;

  .post__cta__text {
    margin: 0.5rem auto 0;
    white-space: nowrap;
  }
`

const ProgressBarWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 0 0.1rem;
  z-index: 10;
  cursor: pointer;
`

const Progress = styled.div`
  width: 100%;
  margin-bottom: 0;
`
